'use client';

import { useState, useCallback } from 'react';
import { authenticatedFetchResponse } from '../lib/api/authenticated-fetch';

interface UseMessageStreamOptions {
  apiBaseUrl?: string;
}

export function useMessageStream(chatId: string, options: UseMessageStreamOptions = {}) {
  const { apiBaseUrl = '/api/meridian' } = options;
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = useCallback(
    async (message: string, onChunk?: (chunk: string) => void) => {
      setIsStreaming(true);
      setError(null);
      try {
        const response = await authenticatedFetchResponse(
          `${apiBaseUrl}/messages?chatId=${encodeURIComponent(chatId)}`,
          {
            method: 'POST',
            body: JSON.stringify({ message }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (response.body) {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value);
            onChunk?.(chunk);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send message');
      } finally {
        setIsStreaming(false);
      }
    },
    [chatId, apiBaseUrl]
  );

  return { isStreaming, error, sendMessage };
}
